import type { U2000CriteriaLookupConfig } from '../../basic/U2000BasicTableList';
export type { U2000CriteriaLookupConfig };
import type { U2000AutoCompleteSearchResult } from '../../../../types/U2000AutoCompleteSearchResult';

export const U2000_CRITERIA_LOOKUP_FIND_PROJECT = 'U3129/findProject';
/** Autocomplete employé feuille de temps — POST /U3129/findEmployee (critère liste U3129). */
export const U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE = 'U3129/findEmployee';
/** Autocomplete employé approbation — POST /U3130/findEmployee (critère liste U3130, BL-0392). */
export const U2000_CRITERIA_LOOKUP_U3130_FIND_EMPLOYEE = 'U3130/findEmployee';
/** Autocomplete projet actifs org — POST /U3121/findProject (critères U3121/U3125/U3139, sans scope FT). */
export const U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT = 'U3121/findProject';
/** Autocomplétion via POST /U3125/search (handler `useU3125FindInvoiceHandlers`). */
export const U2000_CRITERIA_LOOKUP_FIND_INVOICE = 'U3125/findInvoiceNumber';
export const U2000_CRITERIA_LOOKUP_U3141_FIND_PROJECT = 'U3141/findProject';
export const U2000_CRITERIA_LOOKUP_U3120_FIND_CUSTOMER = 'U3120/findCustomer';
export const U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE = 'U3101/findEmployee';
export const U2000_CRITERIA_LOOKUP_U3143_FIND_DISTRICTS = 'U3143/findDistricts';
export const U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE = 'U3134/findInvoice';
export const U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT = 'U3134/findProject';
/** Autocomplete employé TEC — POST /U3134/findEmployee (critère liste U3134, BL-0426). */
export const U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE = 'U3134/findEmployee';
export const U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER = 'U3121/findCustomer';
export const U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT_MANAGER = 'U3121/findProjectManager';
export const U2000_CRITERIA_LOOKUP_U3121_FIND_LAND_SURVEYOR = 'U3121/findLandSurveyor';
export const U2000_CRITERIA_LOOKUP_U3139_FIND_EMPLOYEE = 'U3139/findEmployee';
export const U2000_CRITERIA_LOOKUP_U3139_FIND_SEARCH_CADASTRES = 'U3139/findSearchCadastres';
export const U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_LOTS = 'U3142/findSearchLots';
export const U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_DISTRICTS = 'U3142/findSearchDistricts';
export const U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_CADASTRES = 'U3142/findSearchCadastres';
/** Autocomplete catégorie UL — POST /U2101/findLogicalUnitCategory (BL-0309). */
export const U2000_CRITERIA_LOOKUP_U2101_FIND_LOGICAL_UNIT_CATEGORY = 'U2101/findLogicalUnitCategory';

/** Nombre max de suggestions renvoyées par find* (aligné `U2000AutoCompleteInput`, BL-0047). */
export const U2000_CRITERIA_LOOKUP_PAGE_SIZE = 25;

export type U2000CriteriaLookupSearchCriterion = { fieldName: string; value: string };

export type U2000CriteriaLookupHandler = {
  search: (
    criterias: U2000CriteriaLookupSearchCriterion[],
  ) => Promise<unknown[] | U2000AutoCompleteSearchResult<unknown>>;
  itemToValue: (item: unknown, config: U2000CriteriaLookupConfig) => string;
  itemToDisplay: (item: unknown, config: U2000CriteriaLookupConfig) => string;
  itemToSelectedDisplay?: (item: unknown, config: U2000CriteriaLookupConfig) => string;
};

const registry = new Map<string, U2000CriteriaLookupHandler>();

export function registerCriteriaLookup(findMethod: string, handler: U2000CriteriaLookupHandler): void {
  registry.set(findMethod, handler);
}

export function unregisterCriteriaLookup(findMethod: string): void {
  registry.delete(findMethod);
}

export function resolveCriteriaLookup(findMethod: string): U2000CriteriaLookupHandler | undefined {
  return registry.get(findMethod);
}

export function defaultItemToValue(item: unknown, config: U2000CriteriaLookupConfig): string {
  if (item && typeof item === 'object') {
    const rec = item as Record<string, unknown>;
    return String(rec[config.valueKey] ?? '').trim();
  }
  return '';
}

export function defaultItemToDisplay(item: unknown, config: U2000CriteriaLookupConfig): string {
  if (item && typeof item === 'object') {
    const rec = item as Record<string, unknown>;
    return config.displayKeys
      .map((k) => rec[k])
      .filter((v) => v != null && String(v).length > 0)
      .join(' — ');
  }
  return '';
}

/** Libellé court après sélection (chip, input, critères actifs). Défaut : valueKey seul. */
export function defaultItemToSelectedDisplay(item: unknown, config: U2000CriteriaLookupConfig): string {
  const keys = config.selectedDisplayKeys ?? [config.valueKey];
  if (item && typeof item === 'object') {
    const rec = item as Record<string, unknown>;
    return keys
      .map((k) => rec[k])
      .filter((v) => v != null && String(v).length > 0)
      .join(' — ');
  }
  return '';
}

export function resolveItemToSelectedDisplay(
  handler: U2000CriteriaLookupHandler | undefined,
  item: unknown,
  config: U2000CriteriaLookupConfig,
): string {
  return handler?.itemToSelectedDisplay?.(item, config) ?? defaultItemToSelectedDisplay(item, config);
}

export function getCriteriaApiFieldName(field: { name: string; criteriaLookup?: U2000CriteriaLookupConfig }): string {
  return field.criteriaLookup?.criteriaField ?? field.name;
}

/** Opérateurs string avec autocomplete / tags lorsque la colonne a criteriaLookup */
export function isCriteriaLookupAutocompleteOperator(operatorName: string | undefined): boolean {
  return operatorName === 'equals' || operatorName === 'notequals' || operatorName === 'in';
}
